import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { ASSET_TYPE, FIELD_PATHS, FIELD_VALUE_LABELS } from '@funnels/constants';
import { Asset, FormValuesALIEO, ReduxStoreData } from '@funnels/types';
import { formatAreaAddressWithPostcode, getApplicantsFullnameFromOwners } from '@funnels/utils';

import {
  AssetCategoryReadOnlyView,
  AssetDescriptionReadOnlyView,
  AssetOwnershipReadOnlyView,
  AssetValueReadOnlyView,
  FinancialInstitutionReadOnlyView,
} from '../../../Questions';
import Table from '../../../Table';

const HEADERS = [
  { text: 'Category', min: 220, max: 320 },
  { text: 'Description', min: 220, max: 320 },
  { text: 'Owner(s)', min: 240, max: 340 },
  { text: 'Institution', min: 220, max: 320 },
  { text: 'Value', min: 220, max: 320 },
];

function mapState(currentState: ReduxStoreData) {
  const data: FormValuesALIEO = {
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    liabilities: currentState.liabilities.data,
  };
  const error = currentState.applicants.error || currentState.assets.error || currentState.liabilities.error;
  const loading = currentState.applicants.loading + currentState.assets.loading + currentState.liabilities.loading;

  return { data, error, loading };
}

function AssetsTable() {
  const { data } = useSelector(mapState);
  const applicants = get(data, FIELD_PATHS.APPLICANTS(), []);
  const assets = get(data, FIELD_PATHS.ASSETS(), []);

  return (
    <Table title="Assets" headers={HEADERS}>
      {assets.length > 0 && (
        <tbody data-component="table-tbody">
          {assets.map((asset: Asset, index: number) => {
            const type = get({ assets }, FIELD_PATHS.ASSET_TYPE(index));
            let category = get(FIELD_VALUE_LABELS, type, type);

            if (type === ASSET_TYPE.PROPERTY) {
              const address = formatAreaAddressWithPostcode(get({ assets }, FIELD_PATHS.ASSET_ADDRESS(index)));

              if (address) {
                category += ` - ${address}`;
              }
            } else if (type === ASSET_TYPE.VEHICLE) {
              const vehicle = get({ assets }, FIELD_PATHS.ASSET_VEHICLE_MAKE(index));

              if (vehicle) {
                category += ` - ${vehicle}`;
              }
            }

            const description = get({ assets }, FIELD_PATHS.ASSET_DESCRIPTION(index));
            const owners = get({ assets }, FIELD_PATHS.ASSET_OWNERS(index));
            const ownersFullName = getApplicantsFullnameFromOwners(owners, applicants);
            const financialInstitution = get({ assets }, FIELD_PATHS.ASSET_FINANCIAL_INSTITUTION(index));
            const estimatedValue = get({ assets }, FIELD_PATHS.ASSET_ESTIMATED_VALUE(index));

            return (
              <tr key={asset.clientId}>
                <td>
                  <AssetCategoryReadOnlyView
                    value={category}
                    shouldMaskValue={type === ASSET_TYPE.PROPERTY}
                    textSize="md"
                  />
                </td>
                <td>
                  <AssetDescriptionReadOnlyView value={description} textSize="md" />
                </td>
                <td>
                  <AssetOwnershipReadOnlyView value={ownersFullName} textSize="md" />
                </td>
                <td>
                  <FinancialInstitutionReadOnlyView value={financialInstitution} textSize="md" />
                </td>
                <td>
                  <AssetValueReadOnlyView value={estimatedValue} textSize="md" />
                </td>
              </tr>
            );
          })}
        </tbody>
      )}
    </Table>
  );
}

export default AssetsTable;
